import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, Heart, Luggage, Users, Mountain, Coffee, Accessibility, UtensilsCrossed, Landmark, Share2, Check, ArrowRight, Dna } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { TRAVELER_ARCHETYPES, type ArchetypeDefinition } from "@/lib/traveler-archetypes";
import type { TravelerProfile } from "@shared/schema";

const ARCHETYPE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  heart: Heart,
  backpack: Luggage,
  users: Users,
  mountain: Mountain,
  coffee: Coffee,
  accessibility: Accessibility,
  utensils: UtensilsCrossed,
  landmark: Landmark,
};

/** Static "vibe match" percentages per archetype — aspirational, not real data */
const VIBE_MATCH: Record<string, number> = {
  luxury_couple: 68,
  budget_solo: 81,
  family_with_kids: 72,
  adventure_group: 77,
  retiree_slow: 64,
  accessible_traveler: 59,
  foodie_nomad: 73,
  cultural_deep_diver: 70,
};

interface ArchetypeStepProps {
  onSelectArchetype: (profile: TravelerProfile) => void;
  onSkip: () => void;
  isPending: boolean;
}

export const ArchetypeStep = React.memo(function ArchetypeStep({
  onSelectArchetype,
  onSkip,
  isPending,
}: ArchetypeStepProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ArchetypeDefinition | null>(null);
  const [revealVisible, setRevealVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Trigger reveal animation after selection
  useEffect(() => {
    if (selected) {
      const timer = setTimeout(() => setRevealVisible(true), 50);
      return () => clearTimeout(timer);
    }
    setRevealVisible(false);
  }, [selected]);

  const handleCopyResult = useCallback(() => {
    if (!selected) return;
    const text = t("profileCompletion.shareText", {
      archetype: selected.name,
      defaultValue: "I'm a {{archetype}} on Troopn! What's your travel DNA? \uD83C\uDF0D",
    });
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [selected, t]);

  const handleConfirm = useCallback(() => {
    if (selected) {
      onSelectArchetype(selected.profile);
    }
  }, [selected, onSelectArchetype]);

  // --- Reveal screen ---
  if (selected) {
    const IconComponent = ARCHETYPE_ICON_MAP[selected.icon];
    const vibePercent = VIBE_MATCH[selected.id] ?? 70;

    return (
      <div
        className={`flex flex-col items-center text-center gap-5 py-4 transition-all duration-500 ease-out ${
          revealVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"
        }`}
      >
        {/* Result icon with glow */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse" />
          <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-primary">
            {IconComponent && <IconComponent className="h-10 w-10 text-primary" />}
          </div>
        </div>

        {/* Title reveal */}
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {t("profileCompletion.revealLabel", "Your Travel DNA")}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold">
            {t("profileCompletion.revealTitle", { archetype: selected.name, defaultValue: "You're a {{archetype}}!" })}
          </h2>
        </div>

        {/* Description */}
        <p className="text-muted-foreground max-w-xs">
          {selected.description}
        </p>

        {/* Vibe match stat */}
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/20">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">
            {t("profileCompletion.vibeMatch", { percent: vibePercent, defaultValue: "{{percent}}% of festival-goers share your vibe" })}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col w-full gap-3 pt-2">
          <Button
            onClick={handleConfirm}
            disabled={isPending}
            className="w-full gap-2"
            size="lg"
            data-testid="button-confirm-archetype"
          >
            {isPending ? (
              t("profileCompletion.saving", "Saving...")
            ) : (
              <>
                {t("profileCompletion.letsGo", "Let's go!")}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-2"
              onClick={handleCopyResult}
              data-testid="button-share-result"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  {t("profileCompletion.copied", "Copied!")}
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  {t("profileCompletion.shareResult", "Share your result")}
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => setSelected(null)}
              data-testid="button-pick-again"
            >
              {t("profileCompletion.pickAgain", "Pick again")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // --- Selection grid ---
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Dna className="h-5 w-5 text-primary" />
          {t("profileCompletion.archetypeTitle", "Discover Your Travel DNA")}
        </DialogTitle>
        <DialogDescription>
          {t("profileCompletion.archetypeDescription", "Answer 3 questions to find your perfect travel crew")}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {TRAVELER_ARCHETYPES.map((archetype) => {
          const IconComponent = ARCHETYPE_ICON_MAP[archetype.icon];
          return (
            <Card
              key={archetype.id}
              className="p-3 cursor-pointer hover-elevate overflow-visible transition-all duration-200 hover:border-primary/50 hover:shadow-md active:scale-95"
              onClick={() => setSelected(archetype)}
              data-testid={`card-archetype-${archetype.id}`}
            >
              <div className="flex flex-col items-center text-center gap-2">
                {IconComponent && <IconComponent className="h-6 w-6 text-primary" />}
                <p className="text-sm font-medium leading-tight">{archetype.name}</p>
                <p className="text-xs text-muted-foreground leading-snug">{archetype.description}</p>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-center pt-2">
        <Button
          variant="ghost"
          onClick={onSkip}
          disabled={isPending}
          data-testid="button-skip-archetype"
        >
          {t("profileCompletion.skipForNow", "Skip for now")}
        </Button>
      </div>
    </>
  );
});
